import { useEffect, useState } from "react";
import api, { studentPhoto } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, EmptyState, Field } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Coffee, GraduationCap, Gift, Video, CheckCircle2, Search, ChevronDown, ChevronRight, Briefcase } from "lucide-react";
import { TypeQuestions, missingAnswer } from "@/components/TypeQuestions";
import { CC_ROLES } from "@/lib/constants";

const FALLBACK_TYPES = ["Rotation Lunch", "Graduation Video", "Graduation Job Chat"];
const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

// Week bounded by Saturdays: [last Saturday, this/next Saturday)
function weekSaturdays() {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const daysSinceSat = (now.getDay() - 6 + 7) % 7; // Sat=0, Sun=1, ... Fri=6
  const lastSat = new Date(now); lastSat.setDate(now.getDate() - daysSinceSat);
  const thisSat = new Date(lastSat); thisSat.setDate(lastSat.getDate() + 7);
  return { lastSat, thisSat };
}
function inRange(dateStr, from, to) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (isNaN(d)) return false;
  if (from && d < new Date(from)) return false;
  if (to && d > new Date(to)) return false;
  return true;
}

export default function Touchpoints() {
  const { user } = useAuth();
  const isStudent = user.role === "Student";
  const isManager = CC_ROLES.includes(user.role);
  const [rows, setRows] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [schedFor, setSchedFor] = useState(null);
  const [sched, setSched] = useState({});
  const [createOpen, setCreateOpen] = useState(false);
  const [typeOptions, setTypeOptions] = useState([]);
  const [form, setForm] = useState({ type: "", type_answers: {} });

  const types = typeOptions.length ? typeOptions.map((t) => t.label) : FALLBACK_TYPES;
  const chosenType = typeOptions.find((t) => t.label === form.type);

  const load = () => api.get("/touchpoints").then(({ data }) => setRows(data));
  useEffect(() => {
    load();
    api.get("/touchpoint-meta").then(({ data }) => {
      setTypeOptions(data.type_options || []);
      setForm((f) => ({ ...f, type: f.type || (data.types || [])[0] || "Rotation Lunch" }));
    }).catch(() => {});
    api.get("/gift-card-choices").then(({ data }) => setGifts(data.choices || []));
    if (isManager) api.get("/placement-requests").then(({ data }) => setPlacements(data));
  }, []);

  const openSchedule = (t) => { setSchedFor(t); setSched({ scheduled_at: t.scheduled_at || "", gift_card_choice: t.gift_card_choice || "" }); };
  const doSchedule = async () => {
    try {
      await api.post(`/touchpoints/${schedFor.id}/schedule`, sched);
      toast.success("Meeting scheduled & student notified"); setSchedFor(null); load();
    } catch (e) { toast.error("Could not schedule"); }
  };
  const markSent = async (t) => { await api.post(`/touchpoints/${t.id}/gift-card-sent`); toast.success("Gift card marked sent"); load(); };
  const saveComment = async (t, comments) => { await api.patch(`/touchpoints/${t.id}`, { comments, status: "Completed" }); toast.success("Saved"); load(); };
  const studentRespond = async (t, payload) => {
    await api.post(`/touchpoints/${t.id}/student-response`, payload);
    toast.success("Sent! Campus will confirm your lunch."); load();
  };
  const create = async () => {
    const missing = missingAnswer(chosenType, form.type_answers);
    if (missing) return toast.error(`Please answer: ${missing}`);
    try { await api.post("/touchpoints", form); toast.success("Touchpoint created"); setCreateOpen(false); setForm({ type: types[0], type_answers: {} }); load(); }
    catch (e) { toast.error("Could not create"); }
  };

  const TypeIcon = ({ type }) => type === "Rotation Lunch" ? <Coffee className="w-4 h-4" /> : <GraduationCap className="w-4 h-4" />;

  if (isStudent) {
    return (
      <div>
        <PageHeader title="Campus Touchpoints" subtitle="Stay connected with Campus Relations" />
        {rows.length === 0 ? <Card><EmptyState text="No touchpoints yet — we'll reach out during your rotation." /></Card> : (
          <div className="grid md:grid-cols-2 gap-4">
            {rows.map((t) => <StudentCard key={t.id} t={t} gifts={gifts} onRespond={studentRespond} />)}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Student Touchpoints" subtitle="Rotation lunches, gift cards & graduation outreach">
        {isManager && <Button onClick={() => setCreateOpen(true)} className="bg-cc-blue text-white" data-testid="new-touchpoint-btn">New Touchpoint</Button>}
      </PageHeader>
      <Tabs defaultValue="by-student">
        <TabsList className="mb-4">
          <TabsTrigger value="by-student" data-testid="tab-by-student"><Coffee className="w-4 h-4 mr-1" />By Student</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all-touchpoints"><CheckCircle2 className="w-4 h-4 mr-1" />All Touchpoints</TabsTrigger>
        </TabsList>
        <TabsContent value="by-student"><ByStudentView /></TabsContent>
        <TabsContent value="all">
      <Card className="overflow-hidden">
        {rows.length === 0 ? <EmptyState text="No touchpoints yet." /> : (
          <div className="overflow-x-auto cc-scroll -m-5">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-5 py-3">Student</th><th className="text-left px-5 py-3">Type</th>
                  <th className="text-left px-5 py-3">Status</th><th className="text-left px-5 py-3">When</th>
                  <th className="text-left px-5 py-3">Gift card</th><th className="text-left px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id} className="border-t align-top" data-testid={`touchpoint-${t.id}`}>
                    <td className="px-5 py-3 font-medium text-zinc-800">{t.student?.name || "—"}<div className="text-xs text-zinc-400">{t.facility_name || t.discipline}</div></td>
                    <td className="px-5 py-3"><span className="inline-flex items-center gap-1 text-xs"><TypeIcon type={t.type} />{t.type}</span></td>
                    <td className="px-5 py-3"><StatusBadge status={t.status} /></td>
                    <td className="px-5 py-3 font-mono text-xs">{fmt(t.scheduled_at)}</td>
                    <td className="px-5 py-3 text-xs">{t.gift_card_choice || "—"}{t.gift_card_sent && <span className="ml-1 text-cc-green">✓ sent</span>}</td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap gap-1">
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openSchedule(t)} data-testid={`schedule-${t.id}`}><Video className="w-3 h-3 mr-1" />Schedule</Button>
                        {t.daily_room_url && <a href={t.daily_room_url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="h-7 text-xs">Join</Button></a>}
                        {t.gift_card_choice && !t.gift_card_sent && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => markSent(t)} data-testid={`gift-sent-${t.id}`}><Gift className="w-3 h-3 mr-1" />Gift sent</Button>}
                      </div>
                      {(t.status === "Scheduled" || t.status === "Completed") && (
                        <CommentBox t={t} onSave={saveComment} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!schedFor} onOpenChange={(o) => !o && setSchedFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Schedule meeting</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500 mb-2">Creates a Daily.co video meeting and emails the student the link.</p>
          <Field label="Date & time"><Input type="datetime-local" data-testid="sched-datetime" value={sched.scheduled_at || ""} onChange={(e) => setSched({ ...sched, scheduled_at: e.target.value })} /></Field>
          <Field label="Gift card choice">
            <Select value={sched.gift_card_choice} onValueChange={(v) => setSched({ ...sched, gift_card_choice: v })}>
              <SelectTrigger data-testid="sched-gift"><SelectValue placeholder="Select gift card" /></SelectTrigger>
              <SelectContent>{gifts.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSchedFor(null)}>Cancel</Button>
            <Button className="bg-cc-blue text-white" onClick={doSchedule} data-testid="confirm-schedule-btn">Schedule & notify</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Touchpoint</DialogTitle></DialogHeader>
          <Field label="Placement / Student">
            <Select value={form.placement_request_id} onValueChange={(v) => setForm({ ...form, placement_request_id: v })}>
              <SelectTrigger data-testid="tp-placement"><SelectValue placeholder="Select placement" /></SelectTrigger>
              <SelectContent>{placements.map((p) => <SelectItem key={p.id} value={p.id}>{p.student?.name || "UNKNOWN"} — {p.discipline}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Type">
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="tp-type"><SelectValue /></SelectTrigger>
              <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <TypeQuestions option={chosenType} answers={form.type_answers} onChange={(a) => setForm({ ...form, type_answers: a })} testPrefix="tp" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button className="bg-cc-blue text-white" onClick={create} disabled={!form.placement_request_id} data-testid="create-touchpoint-btn">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CommentBox({ t, onSave }) {
  const [v, setV] = useState(t.comments || "");
  return (
    <div className="mt-2 max-w-xs">
      <Textarea className="text-xs" rows={2} placeholder="How is it going? (post-meeting notes)" value={v} onChange={(e) => setV(e.target.value)} data-testid={`tp-comment-${t.id}`} />
      <Button size="sm" variant="outline" className="h-6 text-xs mt-1" onClick={() => onSave(t, v)} data-testid={`tp-comment-save-${t.id}`}>Save note</Button>
    </div>
  );
}

function ByStudentView() {
  const [rows, setRows] = useState([]);
  const [checklistTypes, setChecklistTypes] = useState(FALLBACK_TYPES);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [rotationFilter, setRotationFilter] = useState("all");
  const [quick, setQuick] = useState("none"); // none | rot-week | grad-week
  const [rotFrom, setRotFrom] = useState("");
  const [rotTo, setRotTo] = useState("");
  const [gradFrom, setGradFrom] = useState("");
  const [gradTo, setGradTo] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = () => { setLoading(true); api.get("/students-touchpoints").then(({ data }) => { setRows(data); setLoading(false); }); };
  useEffect(() => {
    load();
    api.get("/touchpoint-meta").then(({ data }) => { if ((data.types || []).length) setChecklistTypes(data.types); }).catch(() => {});
  }, []);

  const states = Array.from(new Set(rows.map((r) => (r.state || "").trim()).filter(Boolean))).sort();
  const { lastSat, thisSat } = weekSaturdays();
  const query = q.trim().toLowerCase();

  const filtered = rows.filter((r) => {
    if (query && !(r.name || "").toLowerCase().includes(query)) return false;
    if (stateFilter !== "all" && (r.state || "").trim() !== stateFilter) return false;
    if (rotationFilter === "yes" && !r.rotation_active) return false;
    if (rotationFilter === "no" && r.rotation_active) return false;
    if (quick === "rot-week" && !inRange(r.rotation_start, lastSat, thisSat)) return false;
    if (quick === "grad-week" && !inRange(r.graduation_date, lastSat, thisSat)) return false;
    if ((rotFrom || rotTo) && !inRange(r.rotation_start, rotFrom, rotTo)) return false;
    if ((gradFrom || gradTo) && !inRange(r.graduation_date, gradFrom, gradTo)) return false;
    return true;
  });

  const toggleCheck = async (student, type, done) => {
    // optimistic
    setRows((prev) => prev.map((s) => s.id === student.id ? { ...s, checklist: { ...s.checklist, [type]: { ...s.checklist[type], done } } } : s));
    try { await api.post(`/students/${student.id}/touchpoint-toggle`, { type, done }); }
    catch (e) { toast.error("Could not update"); load(); }
  };
  const saveNotes = async (student, notes) => {
    try { await api.patch(`/students/${student.id}/touchpoint-notes`, { touchpoint_notes: notes }); toast.success("Notes saved"); }
    catch (e) { toast.error("Could not save notes"); }
  };
  const clearFilters = () => { setQ(""); setStateFilter("all"); setRotationFilter("all"); setQuick("none"); setRotFrom(""); setRotTo(""); setGradFrom(""); setGradTo(""); };

  return (
    <div data-testid="by-student-view">
      <Card className="mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search student name…" className="pl-9" data-testid="ts-search" />
          </div>
          <Select value={stateFilter} onValueChange={setStateFilter}>
            <SelectTrigger className="w-36" data-testid="ts-state"><SelectValue placeholder="State" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All states</SelectItem>
              {states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={rotationFilter} onValueChange={setRotationFilter}>
            <SelectTrigger className="w-40" data-testid="ts-rotation"><SelectValue placeholder="Rotation" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Rotation: All</SelectItem>
              <SelectItem value="yes">On rotation: Yes</SelectItem>
              <SelectItem value="no">On rotation: No</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <Button size="sm" variant={quick === "rot-week" ? "default" : "outline"} className={quick === "rot-week" ? "bg-cc-blue text-white h-8 text-xs" : "h-8 text-xs"} onClick={() => setQuick(quick === "rot-week" ? "none" : "rot-week")} data-testid="ts-quick-rot-week">Rotation starts this week</Button>
          <Button size="sm" variant={quick === "grad-week" ? "default" : "outline"} className={quick === "grad-week" ? "bg-cc-blue text-white h-8 text-xs" : "h-8 text-xs"} onClick={() => setQuick(quick === "grad-week" ? "none" : "grad-week")} data-testid="ts-quick-grad-week">Graduates this week</Button>
          <div className="flex items-center gap-1 text-xs text-zinc-500 ml-2">Rotation start
            <Input type="date" className="h-8 w-36" value={rotFrom} onChange={(e) => setRotFrom(e.target.value)} data-testid="ts-rot-from" />
            <span>→</span>
            <Input type="date" className="h-8 w-36" value={rotTo} onChange={(e) => setRotTo(e.target.value)} data-testid="ts-rot-to" />
          </div>
          <div className="flex items-center gap-1 text-xs text-zinc-500">Grad
            <Input type="date" className="h-8 w-36" value={gradFrom} onChange={(e) => setGradFrom(e.target.value)} data-testid="ts-grad-from" />
            <span>→</span>
            <Input type="date" className="h-8 w-36" value={gradTo} onChange={(e) => setGradTo(e.target.value)} data-testid="ts-grad-to" />
          </div>
          <Button size="sm" variant="ghost" className="h-8 text-xs text-zinc-500" onClick={clearFilters} data-testid="ts-clear">Clear</Button>
        </div>
      </Card>

      <p className="text-xs text-zinc-400 mb-2" data-testid="ts-count">{filtered.length} of {rows.length} students</p>
      {loading ? <Card><EmptyState text="Loading…" /></Card> :
        filtered.length === 0 ? <Card><EmptyState text="No students match your filters." /></Card> : (
        <div className="space-y-2">
          {filtered.map((s) => {
            const doneCount = checklistTypes.filter((t) => s.checklist[t]?.done).length;
            const isOpen = expanded === s.id;
            return (
              <Card key={s.id} className="p-0" data-testid={`ts-student-${s.id}`}>
                <button className="w-full flex items-center gap-3 px-4 py-3 text-left" onClick={() => setExpanded(isOpen ? null : s.id)} data-testid={`ts-expand-${s.id}`}>
                  {isOpen ? <ChevronDown className="w-4 h-4 text-zinc-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-zinc-400 shrink-0" />}
                  <Avatar src={studentPhoto(s.id)} name={s.name} size={32} testId={`ts-avatar-${s.id}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-zinc-800 truncate">{s.name}</span>
                      {s.discipline && <span className="font-mono text-[10px] px-1.5 py-0.5 bg-zinc-100 rounded">{s.discipline}</span>}
                      {s.in_recruiting && <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-cc-blue/10 text-cc-blue rounded"><Briefcase className="w-3 h-3" />In recruiting</span>}
                    </div>
                    <div className="text-xs text-zinc-400 flex flex-wrap gap-x-3">
                      <span>{s.state || "—"}</span>
                      <span>Rotation: {s.rotation_active ? "Yes" : "No"}{s.rotation_start ? ` · starts ${fmtDate(s.rotation_start)}` : ""}</span>
                      <span>Grad {fmtDate(s.graduation_date)}</span>
                    </div>
                  </div>
                  <span className="text-xs text-zinc-500 shrink-0" data-testid={`ts-progress-${s.id}`}>{doneCount}/{checklistTypes.length} done</span>
                </button>
                {isOpen && (
                  <div className="border-t px-4 py-3 grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-zinc-400 mb-2">Touchpoint checklist</p>
                      <div className="space-y-2">
                        {checklistTypes.map((t) => (
                          <label key={t} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`ts-check-${s.id}-${t.replace(/\s+/g, "-")}`}>
                            <Checkbox checked={!!s.checklist[t]?.done} onCheckedChange={(v) => toggleCheck(s, t, !!v)} />
                            <span className={s.checklist[t]?.done ? "text-zinc-800" : "text-zinc-500"}>{t}</span>
                            {s.checklist[t]?.done && <CheckCircle2 className="w-3.5 h-3.5 text-cc-green" />}
                          </label>
                        ))}
                      </div>
                    </div>
                    <NotesBox student={s} onSave={saveNotes} />
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NotesBox({ student, onSave }) {
  const [v, setV] = useState(student.touchpoint_notes || "");
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-zinc-400 mb-2">Notes</p>
      <Textarea rows={4} className="text-sm" placeholder="Notes about this student's touchpoints…" value={v} onChange={(e) => setV(e.target.value)} data-testid={`ts-notes-${student.id}`} />
      <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" onClick={() => onSave(student, v)} data-testid={`ts-notes-save-${student.id}`}>Save notes</Button>
    </div>
  );
}


function StudentCard({ t, gifts, onRespond }) {
  const [when, setWhen] = useState(t.scheduled_at || "");
  const [gift, setGift] = useState(t.gift_card_choice || "");
  const pending = t.type === "Rotation Lunch" && ["Pending", "Requested"].includes(t.status);
  return (
    <Card data-testid={`student-tp-${t.id}`}>
      <div className="flex items-center gap-2 mb-2">
        {t.type === "Rotation Lunch" ? <Coffee className="w-5 h-5 text-cc-blue" /> : <GraduationCap className="w-5 h-5 text-cc-blue" />}
        <h3 className="font-semibold text-zinc-800">{t.type}</h3>
        <span className="ml-auto"><StatusBadge status={t.status} /></span>
      </div>
      {t.status === "Scheduled" ? (
        <div className="text-sm space-y-1">
          <p className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-cc-green" /> Confirmed for {fmt(t.scheduled_at)}</p>
          {t.gift_card_choice && <p className="text-xs text-zinc-500">Gift card: {t.gift_card_choice}</p>}
          {t.daily_room_url && <a href={t.daily_room_url} target="_blank" rel="noreferrer"><Button size="sm" className="bg-cc-blue text-white mt-1"><Video className="w-3 h-3 mr-1" />Join meeting</Button></a>}
        </div>
      ) : pending ? (
        <div className="space-y-2">
          <p className="text-sm text-zinc-500">Pick a day/time and your gift card — we'll confirm your lunch.</p>
          <Field label="Preferred day & time"><Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid={`stu-when-${t.id}`} /></Field>
          <Field label="Gift card choice">
            <Select value={gift} onValueChange={setGift}>
              <SelectTrigger data-testid={`stu-gift-${t.id}`}><SelectValue placeholder="Select gift card" /></SelectTrigger>
              <SelectContent>{gifts.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Button size="sm" className="bg-cc-blue text-white" disabled={!when || !gift} onClick={() => onRespond(t, { scheduled_at: when, gift_card_choice: gift })} data-testid={`stu-submit-${t.id}`}>Send my preference</Button>
        </div>
      ) : (
        <p className="text-sm text-zinc-500">No action needed right now.</p>
      )}
    </Card>
  );
}
